<template>
  <div class="component-draw-in-freeze">
    <div :style="containerStyle" class="canvas-container">
      <span class="freeze-user-tip">
        {{ $t('calling.FreezingOf').replace('XX', freezeUserName) }}...
      </span>
      <i class="line"></i><i class="line"></i><i class="line"></i><i class="line"></i>
      <canvas id="freeze-canvas" width="1280" height="720"></canvas>
    </div>

    <aside :style="{width: (showColorSelector || showWidthSelector) ? '210px' : 'unset'}">
      <ul class="operations" :style="operationsStyle">
          <li class="operation-btn" :class="[showColorSelector ? 'on' : '']">
            <button @click="toggleColorSelector"><svg-icon icon-class="drawn-line-color"></svg-icon></button>
            <span>{{$t('calling.Color')}}</span>
            <i></i>

            <ul v-show="showColorSelector" class="color-list">
              <li v-for="color in colorList" :key="color.name" :class="[color.selected ? 'selected' : '']">
                <i class="color-pointer" :class="[color.name]" @click="selectColor(color.name)"></i>
              </li>
            </ul>
          </li>

          <li class="operation-btn" :class="[showWidthSelector ? 'on' : '']">
            <button @click="toggleWidthSelector"><svg-icon icon-class="drawn-line-width"></svg-icon></button>
            <span>{{$t('calling.Size')}}</span>
            <i></i>

            <ul v-show="showWidthSelector" class="width-list">
              <li v-for="width in widthList" :key="width.name" :class="[width.selected ? 'selected' : '']">
                <i class="width-pointer" :class="[width.name]" @click="selectWidth(width.name)"></i>
              </li>
            </ul>
          </li>

          <li class="operation-btn">
            <button @click="undo"><svg-icon icon-class="draw-undo"></svg-icon></button>
            <span>{{$t('calling.Revoke')}}</span>
          </li>

          <li class="operation-btn">
            <button @click="clear"><svg-icon icon-class="draw-clear"></svg-icon></button>
            <span>{{$t('common.Clear')}}</span>
          </li>

          <li class="operation-btn">
            <button @click="save"><svg-icon icon-class="draw-save"></svg-icon></button>
            <span>{{$t('common.Save')}}</span>
          </li>

          <li class="operation-btn">
            <button @click="close"><svg-icon icon-class="draw-close"></svg-icon></button>
            <span>{{$t('common.Close')}}</span>
          </li>
        </ul>
    </aside>
  </div>
</template>

<script>
import { zip } from 'ramda'
import { format } from 'date-fns'
import Global from '@/global'

const canvasInfo = {
  el: null,
  context: null
}
const tempPointerData = {
  x: [],
  y: []
}
let isDrawing = false
let drawAnimationRequest
let lastDrawTime

const colorValueMap = Object.fromEntries(Object.entries(Global.PUZZLE_COLOR_CODE).map(([key, value]) => [value, key]))
const colorList = ['blue', 'green', 'yellow', 'red', 'purple'].map((name, index) => ({
  name,
  color: Global.DRAW_COLOR_MAP[name],
  value: colorValueMap[name],
  selected: index === 0
}))

const widthList = Object.entries(Global.DRAW_LINE_WIDTH).map(([key, value], index) => ({
  name: key,
  value,
  selected: index === 0
}))

// 初始化canvas相关信息
function initCanvas () {
  const canvasEl = document.getElementById('freeze-canvas')
  canvasInfo.el = canvasEl
  canvasInfo.context = canvasEl.getContext('2d')
}

// 获取鼠标的坐标，值为百分比
function getPointer (e) {
  if (!isDrawing) return
  // 手动截流
  if (lastDrawTime && new Date() - lastDrawTime < 16) {
    return
  }
  lastDrawTime = new Date()
  const { offsetX, offsetY } = e
  const { width: cssWidth, height: cssHeight } = canvasInfo.el.getBoundingClientRect()
  const ratioX = (offsetX < 0 ? 0 : offsetX > cssWidth ? cssWidth : offsetX) / cssWidth
  const ratioY = (offsetY < 0 ? 0 : offsetY > cssHeight ? cssHeight : offsetY) / cssHeight
  if ((ratioX === 0 && ratioY === 0) || (ratioX === 1 && ratioY === 1)) {
    const prevRatioX = tempPointerData.x.slice(-1)[0]
    const prevRatioY = tempPointerData.y.slice(-1)[0]
    if (prevRatioX === ratioX && prevRatioY === ratioY) return
  }
  tempPointerData.x.push(ratioX)
  tempPointerData.y.push(ratioY)
}

export default {
  name: 'DrawInFreeze',

  props: {
    freezeUserName: {
      type: String,
      default: ''
    },
    containerStyle: {
      type: Object,
      default: () => ({})
    },
    operationsStyle: {
      type: Object,
      default: () => ({})
    },
    linesData: {
      type: Object,
      default: () => ({})
    },
    baseMap: {
      default: undefined
    }
  },

  data () {
    return {
      showColorSelector: false,
      showWidthSelector: false,
      lineNumber: 0,
      colorList: colorList,
      widthList: widthList
    }
  },

  computed: {
    lineTransformedToDraw () {
      // 按照时间顺序排序所有线条
      return Object.values(this.linesData).flat().sort((prev, next) => prev.drawTime - next.drawTime)
    },

    selectedColor () {
      return this.colorList.find(color => color.selected)
    },

    selectedWidth () {
      return this.widthList.find(width => width.selected)
    }
  },
  watch: {
    lineTransformedToDraw: {
      handler () {
        this.drawFreeze()
      },
      deep: true
    },
  },

  mounted() {
    initCanvas()
    this.bindCanvasEvent()
  },

  beforeDestroy() {
    this.unbindCanvasEvent()
  },

  methods: {
    // 获取冻屏结束的画面
    getImageFileOfCanvas () {
      const dataurl = canvasInfo.el.toDataURL('image/jpeg')
      const arr = dataurl.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = window.atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], 'base_map_end.jpg', { type: mime })
    },
    // 开启canvas绘制动画
    startDraw () {
      this.$nextTick(() => {
        // drawAnimationRequest = requestAnimationFrame(this.drawFreeze)
        this.drawFreeze()
      })
    },

    // 关闭canvas绘制动画
    stopDraw () {
      cancelAnimationFrame(drawAnimationRequest)
    },

    // 给canvas绑定鼠标事件，鼠标按下开始获取坐标，鼠标抬起停止
    bindCanvasEvent () {
      canvasInfo.el.addEventListener('pointerdown', this.startGetPointer)
      canvasInfo.el.addEventListener('pointerup', this.stopGetPointer)
    },

    // 解绑事件
    unbindCanvasEvent () {
      canvasInfo.el.addEventListener('pointerdown', this.startGetPointer)
      canvasInfo.el.addEventListener('pointerup', this.stopGetPointer)
    },
    pointerMoveCallback (e) {
      this.drawFreeze()
      getPointer(e)
    },

    // 鼠标按下，开始监听鼠标移动并获取坐标
    startGetPointer (e) {
      isDrawing = true
      tempPointerData.x = []
      tempPointerData.y = []
      canvasInfo.el.addEventListener('pointermove', this.pointerMoveCallback)
      canvasInfo.el.setPointerCapture(e.pointerId)
      this.drawFreeze()
    },

    // 鼠标抬起，停止获取坐标
    stopGetPointer (e) {
      isDrawing = false
      canvasInfo.el.removeEventListener('pointermove', this.pointerMoveCallback)
      canvasInfo.el.releasePointerCapture(e.pointerId)
      this.$emit('draw-line-over', {
        posxList: tempPointerData.x,
        posyList: tempPointerData.y,
        color: this.selectedColor.value,
        number: this.lineNumber,
        width: this.selectedWidth.value
      })
      tempPointerData.x = []
      tempPointerData.y = []
      this.drawFreeze()
    },

    drawFreeze () {
      const { el: { width, height }, context } = canvasInfo

      // 绘制底图
      const drawBaseMap = (image) => {
        if (image) {
          const { width, height } = image
          if (width / height < 1280 / 720) {
            const renderWidth = 720 * width / height
            const offsetX = (1280 - renderWidth) / 2
            context.drawImage(image, 0, 0, width, height, offsetX, 0, renderWidth, 720)
          } else if (width / height === 1280 / 720) {
            context.drawImage(image, 0, 0, width, height, 0, 0, 1280, 720)
          } else if (width / height > 1280 / 720) {
            const renderHeight = 1280 * height / width
            const offsetY = (720 - renderHeight) / 2
            context.drawImage(image, 0, 0, width, height, 0, offsetY, 1280, renderHeight)
          }
        }
      }

      // 绘制线条
      const drawLine = (posList, color, lineWidth) => {
        context.lineWidth = lineWidth
        context.strokeStyle = color
        context.beginPath()
        const head = posList.splice(0, 1)
        context.moveTo(head[0] * width, head[1] * height)
        posList.forEach(([x, y]) => {
          context.lineTo(x * width, y * height)
          // context.lineTo(~~(x * width + 0.5), ~~(y * height + 0.5))
        })
        context.lineCap = 'round'
        context.lineJoin = 'round'
        context.stroke()
      }

      context.clearRect(0, 0, width, height)

      drawBaseMap(this.baseMap)

      // 绘制所有线条
      this.lineTransformedToDraw.forEach(line => {
        const posList = zip(line.posxList, line.posyList)
        drawLine(posList, Global.DRAW_COLOR_MAP[Global.PUZZLE_COLOR_CODE[line.colour]], line.width)
      })

      // 绘制当前正在画的线条
      const posList = zip(tempPointerData.x, tempPointerData.y)
      drawLine(posList, this.selectedColor.color, this.selectedWidth.value)

      // 动画
      // drawAnimationRequest = requestAnimationFrame(this.drawFreeze)
    },

    // 切换颜色选择
    toggleColorSelector () {
      this.showColorSelector = !this.showColorSelector
      this.showWidthSelector = false
    },

    // 选中颜色
    selectColor (name) {
      this.colorList.forEach(color => (color.selected = color.name === name))
      this.showColorSelector = false
    },

    // 切换线条粗细选择
    toggleWidthSelector () {
      this.showWidthSelector = !this.showWidthSelector
      this.showColorSelector = false
    },

    // 选中线条粗细
    selectWidth (name) {
      this.widthList.forEach(width => (width.selected = width.name === name))
      this.showWidthSelector = false
    },

    // 撤销
    undo () {
      this.$emit('undo-draw')
    },

    // 清空
    clear () {
      this.$emit('clear-draw')
    },

    // 关闭
    close () {
      this.$emit('close-draw')
    },

    // 保存冻屏为图片
    save () {
      const url = canvasInfo.el.toDataURL()
      let image = document.createElement('a')
      image.href = url
      image.download = 'Screenshot_' + format(new Date(), 'yyyyMMdd-HHmmss') + '.png'
      image.click()
      this.$message.success(this.$t('calling.FreezeScreenSaved'))
      image = null
    }
  }
}
</script>

<style lang="scss" scoped>
.component-draw-in-freeze {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #F7F8FA;

  .canvas-container {
    position: relative;
    background-color: rgba(190, 199, 217, 0.2);
    border-radius: 16px;
    border: 4px solid #236CFF;

    &::before {
      display: block;
      width: 100%;
      padding-top: 56.25%;
      content: '';
    }

    .freeze-user-tip {
      display: block;
      position: absolute;
      top: -2px;
      left: -3px;
      padding: 6px 12px;
      background-color: rgba(35, 108, 255, 0.8);
      border-radius: 16px 0 16px 0;
      font-size: 14px;
      font-weight: 500;
      color: #FFFFFF;
      line-height: 20px;
      z-index: 1;
      pointer-events: none;
    }

    .line {
      display: block;
      position: absolute;
      background-color: #236CFF;

      &:nth-of-type(1) {
        width: 79px;
        height: 8px;
        top: -8px;
        left: 50%;
        transform: translateX(-50%);
      }

      &:nth-of-type(2) {
        width: 8px;
        height: 79px;
        top: 50%;
        right: -8px;
        transform: translateY(-50%);
      }

      &:nth-of-type(3) {
        width: 79px;
        height: 8px;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
      }

      &:nth-of-type(4) {
        width: 8px;
        height: 79px;
        top: 50%;
        left: -8px;
        transform: translateY(-50%);
      }
    }

    > canvas {
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      border-radius: 12px;
    }
  }

  > aside {
    max-height: 100%;
    position: absolute;
    right: 0;
    overflow-y: auto;

    .operations {
      margin: 0;
      padding: 16px 0;
      float: right;
      background-color: #FFFFFF;
      box-shadow: 0 5px 30px 0 rgba(89, 132, 219, 0.08);
      border-radius: 16px 0 0 16px;
      border: 1px solid #EDEFF2;
      border-right: none;
      display: flex;
      flex-flow: column nowrap;
      justify-content: space-between;
      list-style: none;
      user-select: none;

      .operation-btn {
        width: 120px;
        display: flex;
        flex-flow: column nowrap;
        align-items: center;
        position: relative;

        &.on {
          > button {
            color: #fff;
            background-color: #236CFF;

            &:hover {
              background-color: #236CFF;
            }
          }

          > i {
            background-color: #236CFF;
          }
        }

        > button {
          width: 56px;
          height: 56px;
          margin-bottom: 8px;
          padding: 0;
          font-size: 32px;
          background-color: #FFFFFF;
          box-shadow: 0 4px 6px 0 rgba(89, 132, 219, 0.08);
          border: 1px solid #EDEFF2;
          border-radius: 50%;
          cursor: pointer;
          color: #236CFF;

          &:hover {
            background-color: #F3F6FB;
          }
        }

        > span {
          line-height: 20px;
          font-size: 14px;
          color: #000000;
        }

        > i {
          display: block;
          width: 4px;
          height: 79px;
          background-color: #fff;
          position: absolute;
          top: 0;
          right: 0;
        }

        .color-list {
          width: 56px;
          padding: 0;
          margin: 0;
          position: absolute;
          top: 0;
          left: -76px;
          display: flex;
          flex-flow: column nowrap;
          list-style: none;

          > li {
            width: 56px;
            height: 56px;
            display: flex;
            justify-content: center;
            align-items: center;

            &::before {
              content: '';
              display: block;
              width: 100%;
              height: 100%;
              border-radius: 50%;
            }

            .color-pointer {
              width: 24px;
              height: 24px;
              position: absolute;
              box-shadow: 0 4px 6px 0 rgba(89, 132, 219, 0.08);
              border: 2px solid #FFFFFF;
              border-radius: 50%;
              cursor: pointer;

              &.blue {
                background-color: #47B3FF;
              }

              &.green {
                background-color: #6FDE8D;
              }

              &.yellow {
                background-color: #F9DE4A;
              }

              &.red {
                background-color: #EB5841;
              }

              &.purple {
                background-color: #736FDE;
              }
            }

            &.selected {
              &::before {
                background-color: #EBEEF3;
              }

              .color-pointer {
                width: 32px;
                height: 32px;
              }
            }
          }
        }

        .width-list {
          width: 56px;
          padding: 0;
          margin: 0;
          position: absolute;
          top: 0;
          left: -76px;
          display: flex;
          flex-flow: column nowrap;
          list-style: none;

          > li {
            width: 56px;
            height: 56px;
            display: flex;
            justify-content: center;
            align-items: center;

            &::before {
              content: '';
              display: block;
              width: 100%;
              height: 100%;
              border-radius: 50%;
            }

            .width-pointer {
              width: 24px;
              height: 24px;
              position: absolute;
              background-color: #C7C9CD;
              border-radius: 50%;
              cursor: pointer;

              &.s {
                width: 8px;
                height: 8px;
              }

              &.m {
                width: 16px;
                height: 16px;
              }

              &.l {
                width: 24px;
                height: 24px;
              }

              &.xl {
                width: 32px;
                height: 32px;
              }
            }

            &.selected {
              &::before {
                background-color: #EBEEF3;
              }

              .width-pointer {
                background-color: #236CFF;
              }
            }
          }
        }
      }
    }
  }
}
</style>
